import { nanoid, unixTimestamp } from '@scalar-org/entities/utility'
import type { PipelineOptions } from '@scalar-org/ts-helpers'

import { isTokenExpired } from '@/auth'
import { type ErrorMessage, errorEventHandler } from '@/database/error'

export enum SubscriptionTypes {
  Collection = 'collection',
  Document = 'document',
}

/** Options for creating a subscription */
export type SubscriptionOptions<T extends object> = {
  path: string
  type: SubscriptionTypes
} & PipelineOptions<T>

export type SocketEventHandler<T> = {
  /** Handler function to run when event is received */
  handler: (e: T) => void
  /** Data sanitizing function for event data */
  dataModel?: (e: any) => T | null
}

/** Socket event to the server which must include a valid access token */
export type SocketServerEvent<T = unknown> = {
  event: string
  data: T
  timestamp: number
  token: string
}

/** Socket event payload */
export type SocketEvent<T = unknown> = Omit<SocketServerEvent<T>, 'token'>

export type SocketState = {
  id: string
  accessToken?: string
  nextPing: number
  backoffTimer: ReturnType<typeof setTimeout> | -1
  socket?: WebSocket | null
  eventHandlers: { [x: string]: SocketEventHandler<any> }
  /** List of subscriptions by subscribeId */
  subscriptions: Set<string>
  subscriptionOptions: { [subscribeId: string]: SubscriptionOptions<object> }
  pendingMessages: SocketEvent[]
  canSend: () => boolean
  /** Whether updates are currently paused */
  isPaused: boolean
}

/** Socket state handler */
export type SocketStateHandler = {
  /** Socket is opened */
  open: (state: SocketState) => void
  /** Socket is closed */
  close: (state: SocketState) => void
}

const MAX_RECONNECT_INTERVAL = 60 * 1000 // Always check every minute

/**
 * Time in ms to wait before resetting the backoff timer
 * This prevents us from resetting the backoff timer for
 * short lived connections such as failed upgrades with a WS proxy
 */
const RESET_BACKOFF_WAIT = 2000

function defaultSocketState(id = nanoid()): SocketState {
  return {
    id,
    accessToken: '',
    nextPing: 100,
    backoffTimer: -1,
    socket: null,
    eventHandlers: {
      error: errorEventHandler,
    },
    pendingMessages: [],
    subscriptions: new Set(),
    subscriptionOptions: {},
    isPaused: false,
    canSend() {
      return (
        !!this.socket &&
        this.socket.readyState === this.socket.OPEN &&
        !!this.accessToken &&
        !isTokenExpired(this.accessToken) &&
        !this.isPaused
      )
    },
  }
}

/** Create a socket interface */
export function createSocket(
  baseUrl: string,
  platformVersion?: string,
  errorHandler?: SocketEventHandler<ErrorMessage>,
) {
  const state: SocketState = {
    ...defaultSocketState(),
    eventHandlers: {
      error: errorHandler || errorEventHandler,
    },
  }

  const stateHandlers: {
    [x in keyof SocketStateHandler]: Set<SocketStateHandler[x]>
  } = {
    open: new Set([]),
    close: new Set([]),
  }

  /**
   * Add socket state observation listeners. This works
   * across socket instances.
   */
  function addSocketStateListener<K extends keyof SocketStateHandler>(
    key: K,
    handler: SocketStateHandler[K],
  ) {
    stateHandlers[key].add(handler)

    if (key === 'open') {
      ;(handler as SocketStateHandler['open'])(state)
    } else if (key === 'close') {
      ;(handler as SocketStateHandler['close'])(state)
    }
  }

  /** Remove a previously added socket state listener */
  function removeSocketStateListener<K extends keyof SocketStateHandler>(
    key: K,
    handler: SocketStateHandler[K],
  ) {
    stateHandlers[key].delete(handler)
  }

  /** Initialize the socket connection */
  function init(url: string) {
    if (!url) throw new Error('Invalid url provided')

    state.socket = new WebSocket(url)

    state.socket.onopen = async () => {
      console.log('[MONGOBASE-CLIENT - Socket]: Socket started.')

      stateHandlers.open.forEach((handler) => handler(state))

      state.backoffTimer = setTimeout(() => {
        state.nextPing = 100
      }, RESET_BACKOFF_WAIT)

      retryMessageQueue() // When connected we attempt to resend all waiting messages
      resubscribeAll()

      // Send the server version that client is aware of every time that clients connects
      emitEvent({
        event: 'platformVersion',
        data: { version: platformVersion },
        timestamp: unixTimestamp(),
      })
    }

    state.socket.onerror = (evt) => {
      console.log(`[MONGOBASE-CLIENT - Socket]: Socket error. ${evt.type}`)
    }

    state.socket.onclose = () => {
      console.log('[MONGOBASE-CLIENT - Socket]: Socket closed.')

      stateHandlers.close.forEach((handler) => handler(state))

      // For very short lived connections we don't reset the backoff period
      clearTimeout(state.backoffTimer)

      state.nextPing = Math.min(state.nextPing * 2, MAX_RECONNECT_INTERVAL)
      setTimeout(() => {
        console.log(
          '[MONGOBASE-CLIENT - Socket]: Try to reconnect...',
          state.nextPing,
        )
        init(url)
      }, state.nextPing)
    }

    /**
     * Search for a matching event handler when a message is received.
     * Sanitize the data and run the event handler
     */
    state.socket.onmessage = (evt) => {
      /** Reset the backoff timer once we receive the first message */
      state.nextPing = 100

      try {
        const parsed: SocketEvent = JSON.parse(evt.data)

        // If paused we ignore incoming messages
        if (state.isPaused) return

        if (parsed.event && state.eventHandlers[parsed.event]) {
          const model = state.eventHandlers[parsed.event].dataModel
          const data = model ? model(parsed.data) : parsed.data

          if (!data) {
            throw new Error(
              `Could not parse a valid data payload for ${parsed.event}`,
            )
          }
          state.eventHandlers[parsed.event].handler(data)
        }
      } catch (err) {
        console.error(
          '[MONGOBASE-CLIENT - Socket]: Could not handle the server response.',
        )
        console.error(err)
      }
    }
  }

  async function close() {
    state?.socket?.close()
    Object.assign(state, defaultSocketState(state.id))
  }

  /** Emit a websocket event to the server */
  async function emitEvent<DataType>(
    payload: SocketEvent<DataType>,
    options?: { skipQueue?: boolean },
  ) {
    // Either queue the message until connection is complete or immediately send the message
    if (state.canSend() && state.socket) {
      return state.socket.send(
        JSON.stringify({ ...payload, token: state.accessToken }),
      )
    }

    // Drop event when the client is paused
    if (state.isPaused) return

    // Skip queue if event does not need to be retried
    if (options?.skipQueue) return

    return state.pendingMessages.push(payload)
  }

  /** Add a subscribe event that will re-send on socket re-connect */
  function addSubscribeEvent(
    subscribeId: string,
    options: SubscriptionOptions<object>,
  ) {
    // The pending subscription is assigned to state. We either send immediately below OR
    // the event will be emitted on reconnection
    state.subscriptions.add(subscribeId)
    state.subscriptionOptions[subscribeId] = options

    // Only emit the initial event if the socket is in good state.
    if (state.canSend() && state.socket) {
      emitEvent({
        event: 'subscribe',
        data: { subscribeId, ...options },
        timestamp: Date.now(),
      })
    }
  }

  function removeSubscribeEvent(subscribeId: string) {
    // Only emit the initial event if the socket is in good state.

    const options = state.subscriptionOptions[subscribeId]
    if (!options) {
      console.warn(
        '[MONGOBASE-CLIENT - Socket]: Unsubscribe method called multiple times.',
      )
    }
    if (options && state.canSend() && state.socket) {
      emitEvent({
        event: 'unsubscribe',
        data: { subscribeId, ...state.subscriptionOptions[subscribeId] },
        timestamp: Date.now(),
      })
    }

    state.subscriptions.delete(subscribeId)
    delete state.subscriptionOptions[subscribeId]
  }

  function resubscribeAll() {
    state.subscriptions.forEach((subscribeId) =>
      emitEvent({
        event: 'subscribe',
        data: { subscribeId, ...state.subscriptionOptions[subscribeId] },
        timestamp: Date.now(),
      }),
    )
  }

  /** Retry sending all queued messages when we get a new access token or the socket reconnects */
  async function retryMessageQueue() {
    if (state.canSend()) {
      const messages = [...state.pendingMessages]
      state.pendingMessages = []
      messages.forEach((event) => emitEvent(event))
    }
  }

  /** Add a handler to a specific websocket event */
  async function addEventHandler<T>(
    event: string,
    handler: SocketEventHandler<T>['handler'],
    dataModel?: SocketEventHandler<T>['dataModel'],
  ) {
    if (state.eventHandlers[event]) {
      console.warn(
        '[MONGOBASE-CLIENT - Socket]: A duplicate event handler is detected. Updating.',
      )
    }

    state.eventHandlers[event] = {
      handler,
      dataModel,
    }
  }

  /** Remove the event handler for a specific websocket event */
  async function removeEventHandler(event: string) {
    if (state.eventHandlers[event]) {
      delete state.eventHandlers[event]
    }
  }

  /** Update the socket token and try sending any pending messages */
  function updateSocketToken(accessToken: string) {
    state.accessToken = accessToken
    retryMessageQueue()
  }

  // Initialize the instance and return interface
  init(baseUrl)

  setInterval(() => {
    emitEvent(
      {
        event: 'keepAlive',
        data: {
          platformVersion,
        },
        timestamp: Date.now(),
      },
      { skipQueue: true },
    )
  }, 20000)

  return {
    state,
    init,
    close,
    emitEvent,
    addEventHandler,
    removeEventHandler,
    addSubscribeEvent,
    removeSubscribeEvent,
    updateSocketToken,
    addSocketStateListener,
    removeSocketStateListener,
    /** Pause all database updates - messages will be discarded (not queued) */
    pauseDatabase: () => {
      state.isPaused = true
    },
  }
}

export type SocketInstance = ReturnType<typeof createSocket>
